import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { usePopover } from './dialog';

// ── Icon ──────────────────────────────────────────────────────────────────────
// `satisfies`, not a `Record<string, string>` annotation: the annotation widened
// IconName to plain `string`, so a mistyped name compiled and then silently drew
// a '+' through the fallback (DS-19, SH-17). Now a name that isn't a key here is
// a type error at the call site, and there is no fallback to hide one.
const ICON_PATHS = {
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm9 16-4.3-4.3',
  bell: 'M6 8a6 6 0 0 1 12 0v5l1.5 2.5h-15L6 13V8Zm4 10a2 2 0 0 0 4 0',
  message: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  check: 'M5 12l4 4 10-10',
  x: 'M6 6l12 12M18 6 6 18',
  chevronDown: 'm6 9 6 6 6-6',
  chevronRight: 'm9 6 6 6-6 6',
  chevronLeft: 'm15 6-6 6 6 6',
  play: 'M6 4v16l14-8-14-8Z',
  pause: 'M7 5v14M17 5v14',
  stop: 'M6 6h12v12H6z',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 4v5l3 2',
  calendar: 'M4 7h16v13H4zM4 7V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2M8 2v4M16 2v4',
  folder: 'M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6Z',
  file: 'M6 3h8l4 4v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm8 0v4h4',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 9a7 7 0 0 1 14 0',
  users:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm-6 9a6 6 0 0 1 12 0M17 11a3 3 0 1 0 0-6M23 20a6 6 0 0 0-5-5.9',
  layers: 'm12 3 10 5-10 5L2 8l10-5Zm10 9-10 5L2 12m20 4-10 5-10-5',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  list: 'M3 6h18M3 12h18M3 18h18',
  zap: 'M13 2 3 14h8l-1 8 10-12h-8l1-8Z',
  filter: 'M3 5h18l-7 9v5l-4 2v-7L3 5Z',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  // A real cog. This was the hub circle alone, so Organisation read as a bullet
  // in the sidebar and as a lone dot in the collapsed rail (DS-19, SH-17).
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z',
  inbox:
    'M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z',
  trending: 'm23 6-9.5 9.5-5-5L1 18M17 6h6v6',
  gantt: 'M3 5h8M3 10h12M3 15h6M3 20h10',
  moon: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z',
  sun: 'M12 4V2M12 22v-2M4.93 4.93 3.5 3.5M20.5 20.5l-1.43-1.43M4 12H2M22 12h-2M4.93 19.07 3.5 20.5M20.5 3.5l-1.43 1.43M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z',
  home: 'M3 11 12 3l9 8v10a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1V11Z',
  briefcase: 'M4 7h16v13H4zM9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M4 13h16',
  award: 'M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM8.5 13 7 22l5-3 5 3-1.5-9',
  download: 'M12 3v12M5 12l7 7 7-7M3 21h18',
  flag: 'M4 22V4M4 4h13l-2 4 2 4H4',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  arrowRight: 'M5 12h14M12 5l7 7-7 7',
  eye: 'M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
  // The reveal-password toggle drew a padlock for "hidden", which reads as "this
  // field is locked" (SH-13). The eye, struck through.
  eyeOff:
    'M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM3 3l18 18',
  // A place. Event locations drew the chain link even for "Studio 2" (WS-19).
  pin: 'M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12ZM12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
  link: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  // One glyph, one meaning (UI-CONVENTIONS, "Icons mean one thing"). The flag
  // was Report a bug, a time correction, a location and the Leave tab; the
  // lock was Security, the HR-only marker, the Access tab and Lock month
  // (critique 2026-10-05 #7). These four take the meanings that moved off them.
  bug: 'M7.5 11a4.5 4.5 0 0 1 9 0v3.5a4.5 4.5 0 0 1-9 0V11ZM12 11v8M9.5 3.5 11 6.6M14.5 3.5 13 6.6M7.5 13H3.5M16.5 13h4M8.3 8.5 5 6.5M15.7 8.5l3.3-2',
  shield: 'M12 3 5 6v5c0 4.5 3 8.5 7 10 4-1.5 7-5.5 7-10V6l-7-3Z',
  key: 'M8 11a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM10.8 12.2 19.5 3.5M15.5 7.5l2.5 2.5M18 5l2 2',
  // A day off: the calendar struck through, its frame broken where the stroke
  // crosses. It was the calendar with a dash in it, which at 14px beside the
  // Calendar tab was the same glyph (review of 5407e5a).
  leave: 'M8 4h11a1 1 0 0 1 1 1v11M16 20H4V8M10 7h10M8 2v4M16 2v4M3 3l18 18',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  // What's new: a large four-point spark and a small one.
  sparkle:
    'M10 4l1.6 4.4L16 10l-4.4 1.6L10 16l-1.6-4.4L4 10l4.4-1.6L10 4ZM18 14l.8 2.2 2.2.8-2.2.8L18 20l-.8-2.2-2.2-.8 2.2-.8L18 14Z',
} satisfies Record<string, string>;

export type IconName = keyof typeof ICON_PATHS;

// Decorative by default: aria-hidden, and focusable="false" for the SVG tab stop
// old Edge/IE gave every inline <svg>. An icon never names a control — the
// control carries the name (IconButton's required `label`), so a screen reader
// hears "Next month, button", not "button" or a path (DS-08, ME-V1).
export const Icon = ({
  name,
  size = 16,
  stroke = 1.5,
  style = {},
}: {
  name: IconName;
  size?: number;
  stroke?: number;
  style?: CSSProperties;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={style}
  >
    <path d={ICON_PATHS[name]} />
  </svg>
);

// ── Spinner ───────────────────────────────────────────────────────────────────
// The in-button busy mark, moved here from auth-ui so Button can own `loading`.
// It paints in currentColor, so it reads on every Button variant (white on the
// primary gradient, ink on secondary) instead of the two hardcoded palettes it
// had. 1em tracks the button's text size. hf-spin keeps turning under reduced
// motion on purpose: it IS the status (tokens.css).
export const Spinner = (_: {
  /** No longer needed: the spinner follows currentColor. Kept so callers compile. */
  light?: boolean;
}) => (
  <span
    aria-hidden="true"
    style={{
      width: '1em',
      height: '1em',
      flexShrink: 0,
      borderRadius: 'var(--hf-radius-pill)',
      border: '2px solid color-mix(in srgb, currentColor 35%, transparent)',
      borderTopColor: 'currentColor',
      display: 'inline-block',
      animation: 'hf-spin 0.7s linear infinite',
    }}
  />
);

// ── Tone ──────────────────────────────────────────────────────────────────────
// The one tone name → colour map. Badge, AttentionCount, Button and the domain
// tiles each used to carry a private copy in hex/rgba, and the copies drifted: a
// retired lime "accent", sky/iris washes that disagreed with their own text, and
// dark-theme pastels used as text in light theme (amber measured 1.57:1). Every
// value is a token, so a retune in tokens.css reaches every call site.
//   fg — the hue as TEXT (text-safe in both themes) · bg — its wash · bd — its line
// success/warning/danger are aliases of sage/amber/rose, not a second palette.
export type ToneName =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'accent'
  | 'sage'
  | 'amber'
  | 'rose'
  | 'sky'
  | 'lilac'
  | 'iris'
  | 'coral'
  | 'cyan';
type ToneColors = { fg: string; bg: string; bd: string };
const SAGE = { fg: 'var(--hf-sage-text)', bg: 'var(--hf-sage-wash)', bd: 'var(--hf-sage-line)' };
const AMBER = {
  fg: 'var(--hf-amber-text)',
  bg: 'var(--hf-amber-wash)',
  bd: 'var(--hf-amber-line)',
};
const ROSE = { fg: 'var(--hf-rose-text)', bg: 'var(--hf-rose-wash)', bd: 'var(--hf-rose-line)' };
export const TONE: Record<ToneName, ToneColors> = {
  neutral: { fg: 'var(--hf-ink-2)', bg: 'var(--hf-surface-2)', bd: 'var(--hf-rule-2)' },
  // --hf-accent is the FILL; as text it's under AA on dark (3.1:1), hence -text.
  accent: {
    fg: 'var(--hf-accent-text)',
    bg: 'var(--hf-accent-wash)',
    bd: 'var(--hf-accent-line)',
  },
  success: SAGE,
  warning: AMBER,
  danger: ROSE,
  sage: SAGE,
  amber: AMBER,
  rose: ROSE,
  sky: { fg: 'var(--hf-sky-text)', bg: 'var(--hf-sky-wash)', bd: 'var(--hf-sky-line)' },
  lilac: { fg: 'var(--hf-lilac-text)', bg: 'var(--hf-lilac-wash)', bd: 'var(--hf-lilac-line)' },
  iris: { fg: 'var(--hf-iris-text)', bg: 'var(--hf-iris-wash)', bd: 'var(--hf-iris-line)' },
  coral: { fg: 'var(--hf-coral-text)', bg: 'var(--hf-coral-wash)', bd: 'var(--hf-coral-line)' },
  cyan: { fg: 'var(--hf-cyan-text)', bg: 'var(--hf-cyan-wash)', bd: 'var(--hf-cyan-line)' },
};

// A data mark (bar, dot, swatch, ring) is a FILL. Hues take their -text value:
// the raw pastels sit under 3:1 on light cards, and a mark you can't see is no
// mark (WCAG 1.4.11). Accent keeps --hf-accent, the fill, since its -text is a
// different blue; so an "accent" project bar and the accent chrome match. An
// unknown tone (projects.tone is free text) falls back to the accent.
// It lives beside TONE on purpose; TONE's export already opts this file out of
// Fast Refresh, so the rule has nothing further to protect here.
// eslint-disable-next-line react-refresh/only-export-components
export const markColor = (tone: string): string =>
  (tone !== 'accent' && TONE[tone as ToneName]?.fg) || 'var(--hf-accent)';

// ── Button ────────────────────────────────────────────────────────────────────
// Paint lives in tokens.css (.hf-btn[data-variant]), not in style={{}}: an inline
// value beats every stylesheet rule, so the old inline paint left no room for a
// :hover, :active or :disabled state, and `transition: 'all 120ms'` animated
// nothing (DS-14, SH-V2). Inline here is layout only; a caller's `style` still
// wins, as it always did.
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'glass' | 'danger' | 'accent';
type Size = 'sm' | 'md' | 'lg' | 'xl';

export const Button = ({
  variant = 'secondary',
  size = 'md',
  children,
  icon,
  iconRight,
  onClick,
  style = {},
  full,
  disabled,
  focusableWhenDisabled,
  loading,
  ariaLabel,
  ariaHasPopup,
  ariaExpanded,
  title,
  type = 'button',
  className,
}: {
  variant?: ButtonVariant;
  /** 'xl' is the 46px hero action (Check in, the auth screens' Continue) that
   *  was hand-rolled three times because Button had no size for it (SH-18). */
  size?: Size;
  children?: ReactNode;
  icon?: IconName;
  iconRight?: IconName;
  onClick?: () => void;
  style?: CSSProperties;
  full?: boolean;
  disabled?: boolean;
  /** Keep a disabled button in the tab order (aria-disabled, clicks ignored)
   *  when it is the one that has focus as it disables: the Stepper's + at its
   *  max. Native `disabled` drops that focus to <body>. */
  focusableWhenDisabled?: boolean;
  /** Spinner in place of the icon, aria-busy, and inert until it settles. The
   *  "busy ? 'Saving…'" labels 11 call sites hand-built told a screen reader
   *  nothing and never stopped a second click (DS-14). Inert, not `disabled`:
   *  the button just pressed has focus, and a native disabled drops it to
   *  <body>, so the keyboard would start again from the top on every save. */
  loading?: boolean;
  /** The name when the visible text isn't one. Icon-only? Use IconButton. */
  ariaLabel?: string;
  /** For a button that opens something: what, and whether it is open now. */
  ariaHasPopup?: 'dialog' | 'menu' | 'listbox';
  ariaExpanded?: boolean;
  title?: string;
  /** 'button' unless asked: the HTML default is 'submit', so an Add or Cancel
   *  inside a <form> would submit it. */
  type?: 'button' | 'submit' | 'reset';
  /** Added to .hf-btn: 'hf-hit' for a small button a finger must hit. */
  className?: string;
}) => {
  const pads: Record<Size, string> = {
    sm: '6px 10px',
    md: '8px 14px',
    lg: '10px 18px',
    xl: '0 var(--hf-space-5)',
  };
  const fsz: Record<Size, string | number> = { sm: 12, md: 13, lg: 14, xl: 'var(--hf-text-lg)' };
  // Soft: announced and painted as unavailable, still focusable. preventDefault
  // also stops a submit button's form submission, Enter-key submits included.
  const soft = loading || (disabled && focusableWhenDisabled);
  return (
    <button
      type={type}
      className={className ? `hf-btn ${className}` : 'hf-btn'}
      data-variant={variant}
      onClick={soft ? (e) => e.preventDefault() : onClick}
      disabled={disabled && !soft}
      aria-disabled={soft || undefined}
      aria-busy={loading || undefined}
      aria-label={ariaLabel}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: pads[size],
        // ≥46px: the hero action is the one a thumb aims for.
        minHeight: size === 'xl' ? 46 : undefined,
        borderRadius: 'var(--hf-radius-md)',
        fontSize: fsz[size],
        fontWeight: 500,
        fontFamily: 'var(--hf-font-ui)',
        whiteSpace: 'nowrap',
        width: full ? '100%' : 'auto',
        justifyContent: full ? 'center' : 'flex-start',
        ...style,
      }}
    >
      {loading ? <Spinner /> : icon && <Icon name={icon} size={14} />}
      {children}
      {iconRight && <Icon name={iconRight} size={14} />}
    </button>
  );
};

// ── IconButton ────────────────────────────────────────────────────────────────
// An icon-only button, and the only way to make one. `label` is required by the
// type, so an unnamed icon button is a compile error rather than a screen reader
// saying "button": the calendar's prev/next measured {aria: null, title: ''}
// (ME-V1, XA-09). The label is the tooltip too, since the icon alone may not
// say it. Sizes are Design Spec P02: 32 · 36 · 40, and 44 for the phone topbar.
// Under a coarse pointer every size gets a 44px hit area (.hf-hit, tokens.css).
export const IconButton = ({
  icon,
  label,
  size = 32,
  variant = 'ghost',
  onClick,
  disabled,
  focusableWhenDisabled,
  ariaHasPopup,
  ariaExpanded,
}: {
  icon: IconName;
  label: string;
  size?: 32 | 36 | 40 | 44;
  variant?: 'ghost' | 'secondary';
  onClick?: () => void;
  disabled?: boolean;
  focusableWhenDisabled?: boolean;
  ariaHasPopup?: 'dialog' | 'menu' | 'listbox';
  ariaExpanded?: boolean;
}) => (
  <Button
    variant={variant}
    icon={icon}
    ariaLabel={label}
    ariaHasPopup={ariaHasPopup}
    ariaExpanded={ariaExpanded}
    title={label}
    onClick={onClick}
    disabled={disabled}
    focusableWhenDisabled={focusableWhenDisabled}
    className="hf-hit"
    style={{ width: size, height: size, padding: 0, justifyContent: 'center', flexShrink: 0 }}
  />
);

// ── Badge ─────────────────────────────────────────────────────────────────────
export const Badge = ({
  tone = 'neutral',
  children,
  dot,
  style = {},
}: {
  tone?: ToneName;
  children?: ReactNode;
  dot?: boolean;
  style?: CSSProperties;
}) => {
  const t = TONE[tone] ?? TONE.neutral;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px',
        borderRadius: 999,
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1.4,
        fontFamily: 'var(--hf-font-ui)',
        ...style,
      }}
    >
      {dot && <span style={{ width: 6, height: 6, borderRadius: 999, background: t.fg }} />}
      {children}
    </span>
  );
};

// ── AttentionCount ────────────────────────────────────────────────────────────
// The ONLY way to render "N things need your action". See docs/UI-CONVENTIONS.md.
//
// One tone for every queue in the app. There used to be three — rose on the
// bell, amber in HR, accent in the sidebar — for a single meaning, plus a
// `count > 10` threshold that rendered the ordinary case (1–10 items) in neutral
// chrome so a real queue looked like decoration. Amber, always, at any size.
//
// Zero renders nothing: an empty queue is not news, and a row of "0" badges
// teaches people to stop reading them.
export const AttentionCount = ({
  n,
  label,
  variant = 'count',
}: {
  n: number;
  /** What the number counts, for screen readers — e.g. "open bug reports". */
  label: string;
  /** 'dot' for the collapsed sidebar rail, which has no room for a number but
   *  must not be the one place a pending item is invisible. */
  variant?: 'count' | 'dot';
}) => {
  if (!n) return null;
  if (variant === 'dot')
    return (
      <span
        role="status"
        aria-label={`${n} ${label}`}
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          width: 6,
          height: 6,
          borderRadius: 999,
          // -text, not the raw hue: this dot is the only sign of the queue on the
          // rail, and pastel amber is under 3:1 on the light sidebar.
          background: TONE.warning.fg,
        }}
      />
    );
  return (
    <span role="status" aria-label={`${n} ${label}`}>
      <Badge tone="warning" style={{ fontFamily: 'var(--hf-font-mono)', padding: '1px 7px' }}>
        {n > 99 ? '99+' : n}
      </Badge>
    </span>
  );
};

// ── Tabs ──────────────────────────────────────────────────────────────────────
// The pill bar across the top of a screen (People, Organisation, Workspace).
// Where the selected tab lives — state, localStorage, ?tab= — is the screen's
// business. A tab's `badge` must be an <AttentionCount> (docs/UI-CONVENTIONS.md §1).
// The WAI-ARIA tabs pattern: one Tab stop for the whole bar (the selected tab,
// or the first when none is), arrow keys / Home / End move and select.
export const Tabs = <T extends string>({
  label,
  tabs,
  value,
  onChange,
  onPreview,
}: {
  /** What the tabs switch between, for screen readers ("Workspace sections"). */
  label: string;
  tabs: readonly { id: T; label: string; icon: IconName; badge?: ReactNode }[];
  value: T | undefined;
  onChange: (id: T) => void;
  /** The pointer or focus reached a tab: a screen can start loading it. */
  onPreview?: (id: T) => void;
}) => {
  const current = Math.max(
    0,
    tabs.findIndex((t) => t.id === value),
  );
  // The bar scrolls sideways instead of pushing the page wider (Org's nine tabs
  // made /org scroll horizontally at 1024px: ORG-08, XA-14), so the selected tab
  // can sit out of view — after a deep link, or arrowing past the edge. Bring it
  // back by scrolling the bar alone, and only as far as needed. Not
  // scrollIntoView: that scrolls every ancestor too, so a ?tab= change or a
  // Back made from further down the page yanked the whole page up to the bar.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = listRef.current;
    const tab = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !tab) return;
    const l = list.getBoundingClientRect();
    const t = tab.getBoundingClientRect();
    if (t.left < l.left) list.scrollLeft += t.left - l.left;
    else if (t.right > l.right) list.scrollLeft += t.right - l.right;
  }, [value]);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const last = tabs.length - 1;
    const next =
      e.key === 'ArrowRight'
        ? current === last
          ? 0
          : current + 1
        : e.key === 'ArrowLeft'
          ? current === 0
            ? last
            : current - 1
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : -1;
    if (next < 0 || !tabs[next]) return;
    e.preventDefault();
    onChange(tabs[next].id);
    e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };
  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      style={{
        display: 'flex',
        gap: 3,
        padding: 4,
        borderRadius: 10,
        background: 'var(--hf-glass)',
        border: '1px solid var(--hf-rule-2)',
        backdropFilter: 'blur(20px)',
        // Scroll, don't wrap or overflow the page; the thin global scrollbar in
        // tokens.css is the affordance.
        maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      {tabs.map((t, i) => {
        const on = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={i === current ? 0 : -1}
            onClick={() => onChange(t.id)}
            onPointerEnter={onPreview && (() => onPreview(t.id))}
            onFocus={onPreview && (() => onPreview(t.id))}
            // 44px tall under a coarse pointer (tokens.css); the mouse keeps 31.
            className="hf-hit"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '7px 14px',
              borderRadius: 7,
              fontSize: 12.5,
              cursor: 'pointer',
              fontFamily: 'inherit',
              border: 0,
              // A label that wraps doubles the bar's height (HRP-08); a tab that
              // shrinks clips its own label. Neither: the bar scrolls instead.
              whiteSpace: 'nowrap',
              flexShrink: 0,
              background: on ? 'var(--hf-surface-3)' : 'transparent',
              color: on ? 'var(--hf-ink)' : 'var(--hf-ink-2)',
              // Named properties, not `all`: `all` also animated layout.
              transition:
                'background-color var(--hf-dur-hover) var(--hf-ease), color var(--hf-dur-hover) var(--hf-ease)',
            }}
          >
            <span aria-hidden="true" style={{ display: 'inline-flex' }}>
              <Icon
                name={t.icon}
                size={14}
                style={{ color: on ? 'var(--hf-accent-text)' : 'var(--hf-ink-3)' }}
              />
            </span>
            {t.label}
            {t.badge}
          </button>
        );
      })}
    </div>
  );
};

// ── Avatar ────────────────────────────────────────────────────────────────────
export const Avatar = ({ name, size = 28, bg }: { name?: string; size?: number; bg?: string }) => {
  // Same order as the old hex list, so everyone keeps their colour. Sky and iris
  // were stale prototype hexes that disagreed with the tokens; all seven read them now.
  const colors = [
    'var(--hf-lilac)',
    'var(--hf-sky)',
    'var(--hf-sage)',
    'var(--hf-amber)',
    'var(--hf-coral)',
    'var(--hf-iris)',
    'var(--hf-rose)',
  ];
  const initials = (name || 'HF')
    .split(' ')
    .map((x) => x[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const bgc = bg || colors[hash % colors.length];
  // A span, not a div: it sits inside the roster's and team capacity's row
  // <button>s, which hold phrasing content only. inline-flex keeps its box.
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        // color-mix, not a `${hex}CC` suffix: that only works on a hex literal.
        background: `linear-gradient(135deg, ${bgc}, color-mix(in srgb, ${bgc} 80%, transparent))`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#0a0a0f',
        // Initials scale with the circle, never under the 11px floor (§3): a
        // 22px avatar's were 8.4px.
        fontSize: `max(var(--hf-text-2xs), ${size * 0.38}px)`,
        fontWeight: 600,
        flexShrink: 0,
        border: '1px solid rgba(255,255,255,0.08)',
        fontFamily: 'var(--hf-font-ui)',
      }}
    >
      {initials}
    </span>
  );
};

export const AvatarGroup = ({
  names = [],
  max = 3,
  size = 24,
}: {
  names?: string[];
  max?: number;
  size?: number;
}) => {
  const overflow = names.length - max;
  // Spans throughout, like Avatar: a project row's team sits inside its
  // <button>, which holds phrasing content only.
  return (
    <span style={{ display: 'inline-flex' }}>
      {names.slice(0, max).map((n, i) => (
        <span
          key={i}
          style={{
            display: 'inline-flex',
            marginLeft: i ? -8 : 0,
            outline: '2px solid var(--hf-paper)',
            borderRadius: 999,
          }}
        >
          <Avatar name={n} size={size} />
        </span>
      ))}
      {overflow > 0 && (
        <span
          style={{
            marginLeft: -8,
            width: size,
            height: size,
            borderRadius: 999,
            background: 'var(--hf-surface-3)',
            color: 'var(--hf-ink-2)',
            border: '1px solid var(--hf-rule-2)',
            outline: '2px solid var(--hf-paper)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: `max(var(--hf-text-2xs), ${size * 0.34}px)`,
            fontWeight: 500,
          }}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
};

export const Mono = ({
  children,
  dim,
  style = {},
}: {
  children?: ReactNode;
  dim?: boolean;
  style?: CSSProperties;
}) => (
  <span
    style={{
      fontFamily: 'var(--hf-font-mono)',
      fontSize: 11.5,
      color: dim ? 'var(--hf-ink-3)' : 'var(--hf-ink-2)',
      letterSpacing: '0.02em',
      // Figures in a column line up digit for digit (TS-08). JetBrains Mono is
      // tabular already; this holds if the mono stack falls back to a system face.
      fontVariantNumeric: 'tabular-nums',
      ...style,
    }}
  >
    {children}
  </span>
);

export const ProjectID = ({ id, tone }: { id: string; tone?: string }) => (
  <span
    style={{
      fontFamily: 'var(--hf-font-mono)',
      fontSize: 'var(--hf-text-2xs)',
      fontWeight: 500,
      color: tone || 'var(--hf-ink-3)',
      letterSpacing: '0.04em',
    }}
  >
    {id}
  </span>
);

// ── Progress ──────────────────────────────────────────────────────────────────
// Named, it is a progressbar (or a meter, for a measurement like capacity) with
// its value, so the bar is more than a coloured stripe (DS-08). Unnamed, it is
// hidden instead: an unnamed progressbar is an axe violation that plain divs
// never were, and the callers not yet migrated show the number beside the bar.
// The fill is clipped, not resized: a `width` transition is a layout change on
// every frame, the detector's hit. Not scaleX either, which squashed the pill's
// round end to a square below 100%; the clip's `round` keeps it. clip-path
// reads --hf-dur-layout, which reduced motion zeroes (tokens.css).
type ProgressTone = 'accent' | 'sage' | 'amber' | 'rose' | 'sky' | 'lilac';
export const Progress = ({
  value = 0,
  tone = 'accent',
  size = 6,
  showLabel = false,
  label,
  meter,
}: {
  value?: number;
  tone?: ProgressTone | string;
  size?: number;
  showLabel?: boolean;
  /** What the bar measures ("Leave used"). Without it the bar is decorative. */
  label?: string;
  /** role="meter": a level (capacity, utilisation), not progress toward done. */
  meter?: boolean;
}) => {
  const pct = Math.max(0, Math.min(100, value));
  const a11y = label
    ? {
        role: meter ? 'meter' : 'progressbar',
        'aria-label': label,
        'aria-valuenow': pct,
        'aria-valuemin': 0,
        'aria-valuemax': 100,
      }
    : { 'aria-hidden': true as const };
  // Spans with an explicit display, so a bar is valid inside a <button> row.
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
      <span
        {...a11y}
        style={{
          display: 'block',
          flex: 1,
          height: size,
          borderRadius: 999,
          background: 'var(--hf-surface-3)',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            display: 'block',
            height: '100%',
            clipPath: `inset(0 ${100 - pct}% 0 0 round var(--hf-radius-pill))`,
            transition: 'clip-path var(--hf-dur-layout) var(--hf-ease)',
            // A tone name is a mark; anything else is a colour the caller chose.
            background: Object.hasOwn(TONE, tone) ? markColor(tone) : tone,
          }}
        />
      </span>
      {showLabel && (
        <span
          style={{
            fontSize: 11,
            fontFamily: 'var(--hf-font-mono)',
            color: 'var(--hf-ink-2)',
            minWidth: 28,
            textAlign: 'right',
          }}
        >
          {value}%
        </span>
      )}
    </span>
  );
};

// ── Toggle / Input / Checkbox ─────────────────────────────────────────────────
// A switch, announced as one: role="switch" + aria-checked, where aria-pressed
// made it a "toggle button". The name is required by the type — `label` (the
// row's visible text), or `labelledBy` pointing at it — because 6 of the 7 live
// Toggles had none and were read as an unnamed button (DS-08, ORG-06, XA-09).
type ToggleName = { label: string; labelledBy?: never } | { labelledBy: string; label?: never };
export const Toggle = ({
  checked,
  onChange,
  label,
  labelledBy,
  disabled,
  focusableWhenDisabled,
  describedBy,
}: {
  checked?: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
  /** Button's contract: aria-disabled, clicks ignored, still in the tab order —
   *  so a switch that is off for a reason can be reached and that reason read. */
  focusableWhenDisabled?: boolean;
  /** The id of the text that says why it is the way it is (aria-describedby). */
  describedBy?: string;
} & ToggleName) => {
  const soft = disabled && focusableWhenDisabled;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      disabled={disabled && !soft}
      aria-disabled={soft || undefined}
      onClick={() => {
        if (!soft) onChange?.(!checked);
      }}
      // Cursor and the disabled dim live in tokens.css, so a <fieldset disabled>
      // round it dims it too; inline they ignored the fieldset (ORG-03).
      className="hf-switch hf-hit"
      style={{
        width: 32,
        height: 18,
        flexShrink: 0,
        borderRadius: 999,
        position: 'relative',
        background: checked ? 'var(--hf-accent)' : 'var(--hf-surface-3)',
        // rule-control: the off switch's edge is its only boundary (WCAG 1.4.11, 3:1).
        border: '1px solid ' + (checked ? 'transparent' : 'var(--hf-rule-control)'),
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 1,
          left: checked ? 15 : 1,
          width: 14,
          height: 14,
          borderRadius: 999,
          // The knob is white in both themes; accent-ink is that white, as a token.
          background: 'var(--hf-accent-ink)',
        }}
      />
    </button>
  );
};

// A native checkbox. The old one was a bare <button> — no role, no state, no
// name, a 14px target — so the payroll bulk-select used a native input instead,
// rightly (DS-08, HRA-18). Native gives role, state, Space and form semantics for
// free; accent-color paints it in the brand. The label is required: pass
// hideLabel when the row already shows the text, and it stays announced
// (.hf-sr-only). The <label> is the hit area, at least 24×24 (WCAG 2.5.8).
export const Checkbox = ({
  checked,
  onChange,
  label,
  hideLabel,
  disabled,
}: {
  checked?: boolean;
  onChange?: (v: boolean) => void;
  label: string;
  hideLabel?: boolean;
  disabled?: boolean;
}) => (
  <label
    // Cursor and dim from tokens.css (:has(input:disabled)), so a disabled
    // fieldset round it dims it as well as the `disabled` prop does.
    className="hf-check hf-hit"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--hf-space-2)',
      minWidth: 'var(--hf-space-5)',
      minHeight: 'var(--hf-space-5)',
      fontSize: 'var(--hf-text-sm)',
      color: 'var(--hf-ink-2)',
    }}
  >
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.checked)}
      style={{
        width: 'var(--hf-space-4)',
        height: 'var(--hf-space-4)',
        margin: 0,
        accentColor: 'var(--hf-accent)',
        cursor: 'inherit',
        flexShrink: 0,
      }}
    />
    <span className={hideLabel ? 'hf-sr-only' : undefined}>{label}</span>
  </label>
);

// ── Select ───────────────────────────────────────────────────────────────────
// A dropdown in the DOM, because a native <select> popup is drawn by the OS and
// cannot take the app's styling. tokens.css paints `option` so the remaining
// native selects are at least legible, but legible is not the same as ours: the
// panel keeps the platform's white rows, square corners and system highlight.
// This was private to Org Settings, used four times; the compensated-hours
// picklist was reported for exactly the mismatch it already solved.
//
// The keyboard is the WAI-ARIA select-only combobox (DS-04, XA-04, ORG-V1): it
// only ever handled Escape, while aria-haspopup promised arrow keys. Focus stays
// on the trigger and aria-activedescendant names the active option. The arrows,
// Home/End and typing the first letters of a label move it, opening the list if
// it is shut (closed, ArrowDown/Up open on the current value, as the pattern
// does). Enter or Space chooses. Escape closes and stops there, so the dialog
// around a Select no longer closes with it (DS-V1). Tab closes and moves on
// without choosing: the Org settings autosave, and a look is not a change.
// The listbox is a usePopover panel beside the trigger, not a portal to <body>
// at z-index 9999, which a modal <dialog> would have made inert.
export type SelectOption = { value: string; label: string };
export const Select = ({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  align = 'left',
  mono = false,
  maxH = 260,
  id,
  ariaLabel,
  dense,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'aria-required': required,
}: {
  value: string;
  options: SelectOption[];
  onChange: (v: string) => void;
  placeholder?: string;
  align?: 'left' | 'right';
  mono?: boolean;
  maxH?: number;
  /** Lets an existing <label htmlFor> keep working across the swap from <select>. */
  id?: string;
  ariaLabel?: string;
  /** The design's small control (Capacity's filters): 32px, 12px text. */
  dense?: boolean;
  // The rest of a Field's wiring, so `{(control) => <Select {...control} …/>}`
  // announces the Field's hint and error; with `id` alone they were silent.
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}) => {
  const pop = usePopover({ align, maxH, minWidth: 150 });
  const { open, show, close, triggerRef, panelRef, panelId } = pop;
  const autoId = useId();
  const triggerId = id ?? autoId;
  const valueId = `${triggerId}-value`;
  const selected = options.findIndex((o) => o.value === value);
  const [active, setActive] = useState(-1);
  const typed = useRef({ text: '', at: 0 });
  const optId = (i: number) => `${panelId}-${i}`;
  const lit = open && active >= 0 && active < options.length ? active : -1;

  // What names the field. Only the DOM knows its <label> (htmlFor or wrapping),
  // so it is looked up after mount. The list takes the label's name, never the
  // trigger's: a name borrowed from a combobox is its value, and the list read
  // as "Tue". Of a wrapping label only the parts outside this Select count, and
  // the trigger points at them too, or the open list, which sits inside the
  // label, joins the trigger's name ("Week Tue"). With no label at all the
  // trigger is named by its value, as the button it replaced was, rather than
  // not at all (WCAG 4.1.2); those callers owe an ariaLabel.
  const [names, setNames] = useState<{ trigger?: string; list?: string }>({});
  useLayoutEffect(() => {
    const t = triggerRef.current;
    if (!t || ariaLabel) return;
    const label = t.labels?.[0];
    if (!label) return setNames({ trigger: valueId });
    const wraps = label.contains(t);
    const parts = wraps ? [...label.children].filter((c) => !c.contains(t)) : [label];
    parts.forEach((p, i) => {
      if (!p.id) p.id = `${triggerId}-label-${i}`;
    });
    const ids = parts.map((p) => p.id).join(' ') || undefined;
    setNames(wraps ? { trigger: ids, list: ids } : { list: ids });
  }, [ariaLabel, triggerId, triggerRef, valueId]);

  const openAt = (i: number) => {
    setActive(i);
    if (!open) show();
  };
  const choose = (i: number) => {
    const o = options[i];
    if (o) onChange(o.value);
    close();
  };

  // Keep the active option in view as it moves. The list scrolls, not the page.
  useEffect(() => {
    const list = panelRef.current;
    const el = lit >= 0 ? document.getElementById(`${panelId}-${lit}`) : null;
    if (!list || !el) return;
    if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop;
    else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight)
      list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight;
  }, [lit, panelId, panelRef]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    const last = options.length - 1;
    const from = open ? active : selected;
    const move = (i: number) => {
      e.preventDefault();
      if (last >= 0) openAt(Math.max(0, Math.min(last, i)));
    };
    // A space typed mid-word is part of the word ("new y"), not a choice.
    const typing = typed.current.text !== '' && Date.now() - typed.current.at < 500;
    switch (e.key) {
      case 'ArrowDown':
        return move(!open ? Math.max(from, 0) : from + 1);
      case 'ArrowUp':
        return move(!open ? Math.max(from, 0) : from - 1);
      case 'Home':
        return move(0);
      case 'End':
        return move(last);
      case 'Enter':
        e.preventDefault();
        return lit >= 0 ? choose(lit) : move(Math.max(from, 0));
      case 'Escape':
        return pop.triggerProps.onKeyDown(e);
      case 'Tab':
        if (open) close(false);
        return;
      case ' ':
        if (typing) break;
        e.preventDefault();
        return lit >= 0 ? choose(lit) : move(Math.max(from, 0));
    }
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey || last < 0) return;
    // Typeahead. One letter steps past the current option, so pressing it again
    // cycles through the labels that start with it; more letters refine in place.
    const t = typed.current;
    const now = Date.now();
    t.text = now - t.at < 500 ? t.text + e.key.toLowerCase() : e.key.toLowerCase();
    t.at = now;
    const find = (q: string, base: number) => {
      for (let k = 0; k <= last; k++) {
        const i = (base + k) % options.length;
        if (options[i].label.toLowerCase().startsWith(q)) return i;
      }
      return -1;
    };
    const next = from < 0 ? 0 : from + 1;
    let hit = find(t.text, t.text.length === 1 ? next : Math.max(from, 0));
    if (hit < 0 && [...t.text].every((c) => c === t.text[0])) hit = find(t.text[0], next);
    if (hit >= 0) {
      e.preventDefault();
      openAt(hit);
    }
  };

  const cur = options[selected];

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        {...pop.triggerProps}
        type="button"
        id={triggerId}
        role="combobox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : names.trigger}
        aria-haspopup="listbox"
        aria-describedby={describedBy}
        aria-invalid={invalid}
        aria-required={required}
        aria-activedescendant={lit >= 0 ? optId(lit) : undefined}
        onClick={() => (open ? close() : openAt(Math.max(selected, 0)))}
        onKeyDown={onKeyDown}
        // A <button> fires click on Space's keyup: without this, choosing with
        // Space would close the list and the keyup would open it again.
        onKeyUp={(e) => {
          if (e.key === ' ') e.preventDefault();
        }}
        // Its cursor, and its dim under a <fieldset disabled>, are tokens.css's;
        // so is .hf-hit, a 44px area under a finger round the 36px box.
        className="hf-select hf-hit"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          justifyContent: 'space-between',
          background: 'var(--hf-surface)',
          // rule-control, not rule-2: this border is the trigger's only edge, a
          // WCAG 1.4.11 boundary, and rule-2 is 1.3–1.6:1 (UI-CONVENTIONS §3).
          border: `1px solid ${open ? 'var(--hf-accent-line)' : 'var(--hf-rule-control)'}`,
          borderRadius: 8,
          color: 'var(--hf-ink)',
          fontSize: dense ? 12 : 13,
          fontFamily: mono ? 'var(--hf-font-mono)' : 'inherit',
          padding: dense ? '6px 10px' : '9px 11px',
          // No inline outline: it would beat tokens.css's :focus-visible ring,
          // and keyboard users lost their place on every Select (DS-03).
          textAlign: 'left',
          transition: 'border-color 120ms',
        }}
      >
        <span
          id={valueId}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: cur ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
          }}
        >
          {cur ? cur.label : placeholder}
        </span>
        <Icon
          name="chevronDown"
          size={13}
          style={{
            color: 'var(--hf-ink-3)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
      </button>
      {open && (
        <div
          {...pop.panelProps}
          role="listbox"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : names.list}
          // Options take no focus: a press in the list leaves it on the trigger,
          // where the keys are handled.
          onMouseDown={(e) => e.preventDefault()}
          // The list now sits inside the Select, and Org wraps Selects in a
          // <label>: a click on the list's padding would be forwarded to the
          // trigger and shut it. Cancelling the click cancels the forward.
          onClick={(e) => e.preventDefault()}
          style={{
            ...pop.panelProps.style,
            overflowY: 'auto',
            padding: 'var(--hf-space-1)',
            borderRadius: 'var(--hf-radius-card)',
            background: 'var(--hf-paper-2)',
            color: 'var(--hf-ink)',
            border: '1px solid var(--hf-rule-2)',
            boxShadow: 'var(--hf-shadow-pop)',
          }}
        >
          {options.map((o, i) => {
            const chosen = o.value === value;
            const here = i === lit;
            return (
              <button
                key={o.value}
                id={optId(i)}
                type="button"
                role="option"
                aria-selected={chosen}
                tabIndex={-1}
                onClick={() => choose(i)}
                // The pointer moves the active option, as the arrows do, so
                // Enter picks what is highlighted. State, not a style write.
                onMouseMove={() => {
                  if (!here) setActive(i);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--hf-space-2)',
                  padding: 'var(--hf-space-2) var(--hf-space-3)',
                  borderRadius: 'var(--hf-radius-md)',
                  border: 0,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontSize: 'var(--hf-text-sm)',
                  fontFamily: mono ? 'var(--hf-font-mono)' : 'inherit',
                  textAlign: 'left',
                  background: chosen
                    ? 'var(--hf-accent-wash)'
                    : here
                      ? 'var(--hf-surface-2)'
                      : 'transparent',
                  color: chosen ? 'var(--hf-accent-text)' : 'var(--hf-ink-2)',
                  // On the chosen option the wash is already taken; a ring
                  // shows the keyboard is there too.
                  boxShadow: chosen && here ? 'inset 0 0 0 1px var(--hf-accent-line)' : 'none',
                  transition: 'background var(--hf-dur-hover)',
                }}
              >
                <span>{o.label}</span>
                {chosen && <Icon name="check" size={13} style={{ flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
